import { ArrowLeftIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { currentIndex, useHistoryTrailSnapshot } from 'hooks/useHistoryTrail';
import { Link, matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import MainRoutes from 'routes/MainRoutes';
import { getBreadcrumbLeafKey } from 'utils/breadcrumbLeaf';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { resolveBackDelta } from 'utils/useNavigateWithReturnState';

/**
 * The new design's back control on a record page, named by the crumb of the page it returns to: `← DURU-0050` on an
 * employee opened from a case. The breadcrumbs lead up to the record's list; this leads back to wherever the user
 * came from. It returns as `useGoBack` does — a history POP that skips forms and the record's own tabs.
 *
 * Where the history holds nothing sound to return to, such as a deep link, the breadcrumbs lead up instead; under
 * `sm`, where they hide, the control leads up to the record's list in their place, as a link to it.
 */
export default function BackToOrigin() {
    const location = useLocation();
    const navigate = useNavigate();
    const trail = useHistoryTrailSnapshot();

    const match = matchRoutes([MainRoutes], location)?.at(-1);
    if (!match || !getBreadcrumbLeafKey(location.pathname, match.params)) {
        return null;
    }
    const idx = currentIndex();
    // Until the trail records this navigation, the entry at this index may still be the page left behind.
    if (trail[idx]?.url !== `${location.pathname}${location.search}`) {
        return null;
    }
    const delta = resolveBackDelta(trail, idx);
    if (delta !== null) {
        const label = trail[idx + delta]?.label;
        return <BackButton label={label} onClick={() => navigate(delta)} />;
    }
    const section = (match.route as BreadcrumbRoute).breadcrumbs?.at(-1);
    return section ? <BackButton label={t(section.title)} to={section.path} className="sm:hidden" /> : null;
}

type BreadcrumbRoute = { breadcrumbs?: { path: string; title: string }[] };

type BackProps = { label?: string; className?: string } & ({ onClick: () => void } | { to: string });

/** A step back through history is a button; a way up to a page is a link to it. */
function BackButton({ label, className, ...action }: BackProps) {
    return (
        <Button
            variant="ghost"
            size="sm"
            // Shrinks before it slides under a neighbour, down to its arrow: the label truncates away.
            className={cn('text-muted-foreground max-w-64 min-w-9 shrink has-data-[icon=inline-start]:pl-2', className)}
            aria-label={label ? t('back-to', { name: label }) : t('back')}
            title={label ? t('back-to', { name: label }) : undefined}
            {...('to' in action ? { nativeButton: false, role: 'link', render: <Link to={action.to} /> } : action)}
        >
            <ArrowLeftIcon data-icon="inline-start" className="size-4 rtl:-scale-x-100" />
            <span className="truncate">{label ?? t('back')}</span>
        </Button>
    );
}
